import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Alert, Button } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import {
  getCreditPurchases,
  creditPurchaseDetailPath,
  creditsWalletPath,
} from '../../services/CreditService';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CommonTable from '../components/CommonTable/CommonTable';
import {
  formatCreditMoney,
  formatCreditDate,
  formatCreditSource,
  formatCreditStatus,
} from './creditFormatters';
import '../../css/SearchFilterPanel.css';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './CreditWalletPage.css';

const PAGE_SIZE = 20;

function parseYmd(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function toYmd(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function formatPickerDisplayDate(ymd) {
  const date = parseYmd(ymd);
  if (!date) return '';
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${mo}/${day}/${y}`;
}

function CreditHistoryDateField({ label, id, value, onChange, placeholder }) {
  return (
    <div className="filter-group">
      <label htmlFor={id}>{label}</label>
      <DateRangePicker
        initialSettings={{
          singleDatePicker: true,
          autoApply: true,
          locale: { format: 'MM/DD/YYYY' },
          ...(value ? { startDate: parseYmd(value) } : {}),
        }}
        onCallback={(start) => {
          onChange(toYmd(start.toDate()));
        }}
        onApply={(event, picker) => {
          if (picker.startDate) {
            onChange(toYmd(picker.startDate.toDate()));
          }
        }}
      >
        <input
          id={id}
          type="text"
          className="form-control input-daterange-timepicker credit-history-date-input"
          readOnly
          placeholder={placeholder}
          value={formatPickerDisplayDate(value)}
        />
      </DateRangePicker>
    </div>
  );
}

const COLUMNS = [
  {
    key: 'ref',
    header: 'Ref',
    accessor: (row) => `#${row.ref_id}`,
  },
  {
    key: 'date',
    header: 'Purchase date',
    accessor: (row) => formatCreditDate(row.purchase_date),
  },
  {
    key: 'source',
    header: 'Source',
    accessor: (row) => formatCreditSource(row.source),
    cellClassName: 'text-capitalize',
  },
  {
    key: 'purchased',
    header: 'Purchased',
    accessor: (row) => `$${formatCreditMoney(row.amount_purchased_usd)}`,
  },
  {
    key: 'used',
    header: 'Used',
    accessor: (row) => `$${formatCreditMoney(row.amount_used_usd)}`,
  },
  {
    key: 'balance',
    header: 'Balance',
    accessor: (row) => `$${formatCreditMoney(row.balance)}`,
  },
  {
    key: 'validity',
    header: 'Valid until',
    accessor: (row) => formatCreditDate(row.validity_date),
  },
  {
    key: 'status',
    header: 'Status',
    accessor: (row) => formatCreditStatus(row.status),
    cellClassName: 'text-capitalize',
  },
];

export default function CreditPurchaseHistoryPage() {
  const navigate = useNavigate();
  const filterRef = useRef(null);
  const [searchDropDown, setSearchDropDown] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterError, setFilterError] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [draftDateFrom, setDraftDateFrom] = useState('');
  const [draftDateTo, setDraftDateTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const res = await getCreditPurchases({
        limit: PAGE_SIZE,
        offset,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
      });
      if (res?.success) {
        setPurchases(res.purchases || []);
        setTotalPages(res.totalPages || 1);
        setTotal(res.total || 0);
      } else {
        setError(res?.message || 'Failed to load purchase history');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  }, [page, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideDatePicker = !!(event.target.closest && (
        event.target.closest('.daterangepicker') ||
        event.target.closest('.drp-buttons') ||
        event.target.closest('.calendar-table')
      ));

      if (filterRef.current && !filterRef.current.contains(event.target) && !clickedInsideDatePicker) {
        setSearchDropDown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(creditsWalletPath());
    }
  };

  const handleApplyFilters = () => {
    if (draftDateFrom && draftDateTo && draftDateFrom > draftDateTo) {
      setFilterError('Start date must be on or before end date.');
      return;
    }
    setFilterError(null);
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setPage(1);
    setSearchDropDown(false);
  };

  const handleResetFilters = () => {
    setFilterError(null);
    setDraftDateFrom('');
    setDraftDateTo('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    setSearchDropDown(false);
  };

  const toggleFilters = () => {
    setSearchDropDown((open) => {
      if (!open) {
        setDraftDateFrom(dateFrom);
        setDraftDateTo(dateTo);
      }
      return !open;
    });
  };

  const hasActiveFilters = Boolean(dateFrom || dateTo);

  return (
    <div className="credit-wallet-page">
      <button type="button" className="credit-wallet-back-button" onClick={handleBack}>
        <FiArrowLeft style={{ marginRight: '0.5rem' }} aria-hidden="true" />
        Back
      </button>

      <Row className="credit-wallet-row">
        <Col lg={12}>
          <div className="user-list-card credit-wallet-main-card">
            <div className="credit-wallet-header">
              <button
                type="button"
                className={`credit-history-filter-toggle${hasActiveFilters ? ' credit-history-filter-toggle--active' : ''}`}
                onClick={toggleFilters}
                aria-expanded={searchDropDown}
              >
                <i className="fas fa-filter me-2" aria-hidden="true" />
                Filters
                {hasActiveFilters && <span className="credit-history-filter-badge" aria-hidden="true" />}
              </button>
            </div>

            {searchDropDown && (
              <div className="credit-history-filter-panel">
                <div className="filter-wrapper" ref={filterRef}>
                  <div className="content">
                    <div className="main-filters">
                      <div className="filter-form credit-history-filter-form">
                        <div className="filter-row credit-history-filter-row">
                          <CreditHistoryDateField
                            id="credit-history-date-from"
                            label="Purchase date from"
                            value={draftDateFrom}
                            onChange={setDraftDateFrom}
                            placeholder="Select start date"
                          />
                          <CreditHistoryDateField
                            id="credit-history-date-to"
                            label="Purchase date to"
                            value={draftDateTo}
                            onChange={setDraftDateTo}
                            placeholder="Select end date"
                          />
                        </div>
                        {filterError && (
                          <Alert variant="warning" className="credit-history-filter-error mb-0">
                            {filterError}
                          </Alert>
                        )}
                      </div>

                      <div className="filter-actions credit-history-filter-actions">
                        <button type="button" className="search-btn" onClick={handleApplyFilters}>
                          <i className="fas fa-search me-2" aria-hidden="true" />
                          Apply
                        </button>
                        <button
                          type="button"
                          className="reset-btn"
                          onClick={handleResetFilters}
                          disabled={!draftDateFrom && !draftDateTo && !hasActiveFilters}
                        >
                          <i className="fas fa-undo me-2" aria-hidden="true" />
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="credit-wallet-content">
              {loading && (
                <div className="loading-container credit-wallet-loading">
                  <div className="loading-spinner" />
                </div>
              )}

              {!loading && error && (
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              )}

              {!loading && !error && (
                <>
                  <div className="row g-0 credit-wallet-table-row">
                    <CommonTable
                      columns={COLUMNS}
                      data={purchases}
                      onRowClick={(row) => navigate(creditPurchaseDetailPath(row.ref_id))}
                      emptyMessage="No purchases yet"
                      emptyStateIcon="fas fa-receipt"
                      getRowId={(row) => row.ref_id}
                      minHeight="0"
                      maxHeight="none"
                      className="credit-wallet-common-table"
                    />
                  </div>

                  {totalPages > 1 && (
                    <div className="credit-pagination">
                      <span className="credit-pagination-meta">
                        Page {page} of {totalPages} ({total} items
                        {hasActiveFilters ? ', filtered' : ''})
                      </span>
                      <div className="credit-pagination-actions">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="ms-2"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <InternalFooter />
    </div>
  );
}
